import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useToast } from '@chakra-ui/react'
import mixpanel from 'mixpanel-browser'

import { useCreateAiOperatorFeedbackMutation } from '@/graphql/generated/hooks'
import { IncidentActionType } from '@/graphql/generated/schemas'
import { isAIOperator } from '@/utils/incidents'

import {
  FeedbackType,
  UseAiOperatorFeedbackParamsIProps,
  UseAiOperatorFeedbackReturnIProps,
} from '../types'
import { FEEDBACK_CONSTANTS } from '../utils'

/**
 * Custom hook for managing AI Operator Feedback component state and logic
 *
 * @param incident - The incident data from GraphQL
 * @returns Object containing all state, computed values, and handlers
 */
export const useAiOperatorFeedback = ({
  incident,
}: UseAiOperatorFeedbackParamsIProps): UseAiOperatorFeedbackReturnIProps => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const toast = useToast()
  const [createAiOperatorFeedback] = useCreateAiOperatorFeedbackMutation()

  // Track incident changes for reset
  const prevIncidentRef = useRef(incident)

  const isProcessedByAiOperator = incident?.wasProcessedByAiOperator || false

  // We cannot rely on incident.isFlagged because it gets reset to false when a human operator acknowledges the incident.
  const isFlagged = useMemo(() => {
    if (!incident?.actions) return false

    return incident.actions.some(
      (action) =>
        action?.type === IncidentActionType.Flag &&
        action?.creator &&
        isAIOperator(action.creator)
    )
  }, [incident?.actions])

  // Always show feedback for AI-processed incidents to maximize feedback collection
  const shouldShow = useMemo(
    () => isProcessedByAiOperator,
    [isProcessedByAiOperator]
  )

  // Helper function to reset component to initial state
  const resetToInitialState = useCallback(() => {
    setIsExpanded(false)
    setFeedbackType(null)
    setFeedbackMessage('')
    setIsSubmitting(false)
    setIsSubmitted(false)
  }, [])

  // Reset when incident changes after feedback submission
  useEffect(() => {
    const prevIncident = prevIncidentRef.current

    if (!isSubmitted || !prevIncident || !incident) {
      prevIncidentRef.current = incident
      return
    }

    const hasIncidentChanged = prevIncident !== incident

    if (hasIncidentChanged) {
      resetToInitialState()
    }

    prevIncidentRef.current = incident
  }, [incident, isSubmitted, resetToInitialState])

  const handleThumbClick = useCallback(
    (type: FeedbackType) => {
      setFeedbackType(type)
      setIsExpanded(true)

      // Track thumb click event based on incident type and feedback type
      const eventName = isFlagged
        ? `Button press: AIO feedback flagged thumbs-${
            type === 'positive' ? 'up' : 'down'
          }`
        : `Button press: AIO feedback resolved thumbs-${
            type === 'positive' ? 'up' : 'down'
          }`

      mixpanel.track(eventName, {
        incident_id: incident?.id,
        feedback_type: type,
        is_flagged: isFlagged,
      })
    },
    [incident?.id, isFlagged, isProcessedByAiOperator]
  )

  const handleSubmit = useCallback(async () => {
    if (!feedbackType || !incident?.id) return

    setIsSubmitting(true)

    try {
      const { data } = await createAiOperatorFeedback({
        variables: {
          input: {
            incidentId: incident.id,
            isPositive: feedbackType === 'positive',
            message: feedbackMessage.trim() || null,
          },
        },
      })

      if (data?.createAiOperatorFeedback?.aiOperatorFeedback) {
        // Track feedback submission
        const eventName = 'Button press: AIO feedback submit'

        mixpanel.track(eventName, {
          incident_id: incident.id,
          feedback_type: feedbackType,
          is_flagged: isFlagged,
          message: feedbackMessage.trim() || null,
        })

        setIsSubmitted(true)
      } else {
        throw new Error('Failed to create feedback')
      }
    } catch (error: any) {
      // Handle specific error cases from backend
      let errorDescription = FEEDBACK_CONSTANTS.MESSAGES.ERROR_DESCRIPTION

      if (
        error?.graphQLErrors?.[0]?.extensions?.code === 'incident.not.found'
      ) {
        errorDescription = 'The incident has not been found'
      } else if (
        error?.graphQLErrors?.[0]?.message?.includes('invalid.input.message')
      ) {
        errorDescription = `Feedback message is too long (max ${FEEDBACK_CONSTANTS.MAX_MESSAGE_LENGTH} characters)`
      }

      toast({
        title: FEEDBACK_CONSTANTS.MESSAGES.ERROR_TITLE,
        description: errorDescription,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    feedbackType,
    incident?.id,
    feedbackMessage,
    toast,
    createAiOperatorFeedback,
  ])

  return {
    isExpanded,
    feedbackType,
    feedbackMessage,
    isSubmitting,
    isSubmitted,
    shouldShow,
    isFlagged,
    handleThumbClick,
    handleSubmit,
    setFeedbackMessage,
  }
}
